// Component: SummaryStrip.jsx
// Purpose: Summary metric cards for Algo Trading hub (P&L, strategies, positions, win rate)

import React from 'react';

/*>>> CARD DEFINITIONS <<<*/
const cardConfigs = [
  {
    id: 'pnl',
    label: 'TOTAL P&L',
    getValue: (d) => {
      const val = d.totalPnl ?? 0;
      const sign = val >= 0 ? '+' : '';
      return `${sign}${val.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}`;
    },
    getSubText: (d) => {
      const livePnl = d.livePnl ?? 0;
      const paperPnl = d.paperPnl ?? 0;
      const fmt = (v) => `${v >= 0 ? '+' : ''}${v.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}`;
      if (d.liveCount > 0 && d.paperCount > 0) {
        return `Live: ${fmt(livePnl)} / Paper: ${fmt(paperPnl)}`;
      }
      return 'Today';
    },
    getColor: (d) => (d.totalPnl >= 0 ? 'text-profit' : 'text-loss'),
    icon: (d) => (
      <div className={`p-2 rounded-lg ${d.totalPnl >= 0 ? 'bg-profit/10' : 'bg-loss/10'}`}>
        <svg className={`w-4 h-4 ${d.totalPnl >= 0 ? 'text-profit' : 'text-loss'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          {d.totalPnl >= 0 ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M2 20l5.5-5.5m0 0l3 3 7-7M7.5 14.5L20 2" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" d="M2 4l5.5 5.5m0 0l3-3 7 7M7.5 9.5L20 22" />
          )}
        </svg>
      </div>
    ),
  },
  {
    id: 'strategies',
    label: 'ACTIVE STRATEGIES',
    getValue: (d) => String(d.activeStrategies ?? 0),
    getSubText: (d) => `${d.liveCount ?? 0} live / ${d.paperCount ?? 0} paper`,
    getColor: () => 'text-fg-1',
    icon: () => (
      <div className="p-2 rounded-lg bg-primary/10">
        <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
    ),
  },
  {
    id: 'positions',
    label: 'OPEN POSITIONS',
    getValue: (d) => String(d.openPositions ?? 0),
    getSubText: (d) => {
      const instruments = d.positionInstruments ?? [];
      if (instruments.length === 0) return 'No positions';
      if (instruments.length <= 2) return instruments.join(', ');
      return `${instruments.slice(0, 2).join(', ')} +${instruments.length - 2}`;
    },
    getColor: () => 'text-fg-1',
    icon: () => (
      <div className="p-2 rounded-lg bg-[color-mix(in_oklch,var(--theme-purple)_12%,transparent)]">
        <svg className="w-4 h-4 text-[var(--theme-purple)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      </div>
    ),
  },
  {
    id: 'winrate',
    label: 'WIN RATE',
    getValue: (d) => `${d.winRate ?? 0}%`,
    getSubText: (d) => `${d.wins ?? 0}W / ${d.losses ?? 0}L`,
    getColor: (d) => {
      const rate = d.winRate ?? 0;
      if (rate >= 60) return 'text-profit';
      if (rate >= 40) return 'text-warning';
      return 'text-loss';
    },
    icon: (d) => {
      const rate = d.winRate ?? 0;
      let colorClass = 'text-profit bg-profit/10';
      if (rate < 60) colorClass = 'text-warning bg-warning/10';
      if (rate < 40) colorClass = 'text-loss bg-loss/10';
      const parts = colorClass.split(' ');
      return (
        <div className={`p-2 rounded-lg ${parts[1]}`}>
          <svg className={`w-4 h-4 ${parts[0]}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
    },
  },
];

function SummaryStrip({ data = {} }) {
  return (
    <div className="grid grid-cols-4 gap-3">
      {cardConfigs.map((card, index) => (
        <div
          key={card.id}
          className="
            group relative rounded-xl border border-[var(--divider)]
            bg-bg-2
            p-4 transition-all duration-300 ease-out
            hover:border-[var(--divider)] dark:hover:border-[var(--divider)]
          "
        >
          {/* --> Header row: icon + label */}
          <div className="flex items-center justify-between mb-3">
            <span className="ds-eyebrow text-fg-3">
              {card.label}
            </span>
            {card.icon(data)}
          </div>

          {/* --> Primary value */}
          <div className={`${card.id === 'pnl' ? 'text-3xl' : 'text-2xl'} font-bold tracking-tight ds-tnum ${card.getColor(data)}`}>
            {card.getValue(data)}
          </div>

          {/* --> Sub text */}
          <div className="mt-1 text-xs text-fg-3">
            {card.getSubText(data)}
          </div>
        </div>
      ))}

    </div>
  );
}

export default SummaryStrip;
